import React, { useRef, useState, useMemo } from "react";
import { Row, Col, Button, Modal } from "antd";
import { EyeOutlined, UploadOutlined, FileOutlined, DeleteOutlined } from "@ant-design/icons";
import useCartStore from "../../../../stores/useCartStore";
import { toCamelCase, normalizeStaticSelectValue } from "./customerFormUtils";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useDocumentViewer from "./useDocumentViewer";

/**
 * Sticky footer bar for AddCustomerDrawer containing Cancel and
 * Register/Update Customer buttons with all submit / validation logic.
 */
const CustomerDrawerFooter = ({
  // form refs
  customerForm,
  // flags
  isSubmittingCustomer,
  setIsSubmittingCustomer,
  isPanMandatoryNow,
  enteredPAN,
  isPanUpdateMode,
  setIsPanUpdateMode,
  isAddNewCustomer,
  isNriBilling,
  isCreditSale,
  // drawer state
  retailCustomerFields,
  retailCustomerTabs,
  activeTab,
  setActiveTab,
  setActiveTabName,
  isB2BEnabled,
  formDataCache,
  setFormDataCache,
  groupedFields,
  // customer actions
  addNewCustomer,
  editOldCustomer,
  addCustomer,
  retailCustomerGroups,
  // close / reset
  setDisplayAddNewCustomer,
  setIsB2BEnabled,
  setPanCustomerInfo,
  setToggleCustomer,
  // sales rep
  cart,
  tillData,
  posConfig,
  setOpenSalesRep,
  isPanCustomer,
  panCustomerInfo,
  panThreshold,
  // document management
  documents,
  setDocuments,
  handleFileUpload,
  isViewMode,
}) => {
  const isLocked = useRef(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const { openDocument, docViewer, closeDocViewer } = useDocumentViewer();

  const handleSubmit = async () => {
    if (retailCustomerFields && retailCustomerFields.length > 0) {
      if (isLocked.current) return;
      isLocked.current = true;
      setIsSubmittingCustomer(true);

      try {
        // ── Stage 1: Pre-processing ──
        const currentTabFormValues = customerForm.getFieldsValue(true);

        // Merge current tab into cache + autofill
        const allCachedValues = {
          ...Object.values(formDataCache).reduce((acc, tabVals) => ({ ...acc, ...tabVals }), {}),
          ...currentTabFormValues,
        };
        const getValueFromMerged = (fieldKey) => {
          if (allCachedValues[fieldKey]) return allCachedValues[fieldKey];
          for (const [key, val] of Object.entries(allCachedValues)) {
            if (/^group_\d+$/.test(key) && val?.[fieldKey]) return val[fieldKey];
          }
          return null;
        };
        const crossTabPan = getValueFromMerged("panCard");
        const crossTabGst = getValueFromMerged("gstNo") || getValueFromMerged("gstNumber");

        const updatedCache = {
          ...formDataCache,
          [activeTab]: { ...(formDataCache[activeTab] || {}), ...currentTabFormValues },
        };

        // Auto-fill business address from contact if PAN but no GST
        if (isPanCustomer && !crossTabGst && retailCustomerGroups) {
          const contactTabIds = retailCustomerTabs.filter((t) => t.visible && t.name?.toLowerCase().includes("contact")).map((t) => t.id);
          const businessTabs = retailCustomerTabs.filter((t) => t.visible && t.name?.toLowerCase().includes("business"));
          const contactGroups = (retailCustomerGroups || []).filter((g) => contactTabIds.includes(g.tabId) && g.name?.toLowerCase().includes("address"));
          const businessGroups = (retailCustomerGroups || []).filter((g) => businessTabs.some((bt) => bt.id === g.tabId) && g.name?.toLowerCase().includes("address"));
          const allCachedFlat = Object.values(updatedCache).reduce((acc, v) => ({ ...acc, ...v }), {});
          let contactAddr = null;
          for (const cg of contactGroups) {
            const gd = updatedCache[contactTabIds[0]]?.[cg.id] || allCachedFlat[cg.id];
            if (gd && typeof gd === "object" && Object.values(gd).some((v) => v?.toString().trim())) {
              contactAddr = gd;
              break;
            }
          }
          if (contactAddr && businessGroups.length > 0) {
            for (const bt of businessTabs) {
              if (!updatedCache[bt.id]) updatedCache[bt.id] = {};
              const bGroups = businessGroups.filter((g) => g.tabId === bt.id);
              for (const bg of bGroups) {
                const existing = updatedCache[bt.id][bg.id];
                if (!existing || !Object.values(existing).some((v) => v?.toString().trim())) {
                  updatedCache[bt.id][bg.id] = { ...contactAddr };
                }
              }
            }
          }
        }

        setFormDataCache(updatedCache);

        // b2c_customer.name is the only NOT NULL identity column the backend actually
        // requires (verified against the schema -- mobile/email/firstName/lastName/
        // taxId are all nullable). Ask for it directly instead of substituting
        // Business-tab data into it (removed BUG-2113/2156 fallback, which silently
        // wrote the company name into this B2C-only field to dodge this same check).
        if (!getValueFromMerged("name")?.toString().trim()) {
          Modal.warning({
            title: "Name Required",
            content: "Please enter the customer's name to proceed.",
            okText: "OK",
            cancelButtonProps: { style: { display: "none" } },
          });
          setIsSubmittingCustomer(false);
          isLocked.current = false;
          return;
        }

        // ── Validate fields (AFTER preprocessing + autofill) ──
        const validatedValues = await customerForm.validateFields().catch(() => null);
        if (!validatedValues) {
          setIsSubmittingCustomer(false);
          isLocked.current = false;
          return;
        }

        // ── PAN threshold validation ──
        if (!isNriBilling && (isPanCustomer || isPanMandatoryNow) && !getValueFromMerged("panCard")?.trim()) {
          const thresholdMsg = panThreshold
            ? `PAN is mandatory for transactions above ₹${Number(panThreshold).toLocaleString("en-IN")}. Please enter PAN number to proceed.`
            : "Please enter PAN number to proceed.";
          Modal.warning({ title: "PAN Required", content: thresholdMsg, okText: "OK", cancelButtonProps: { style: { display: "none" } } });
          setIsSubmittingCustomer(false);
          isLocked.current = false;
          return;
        }

        // ── PAN API validation ──
        if (!isNriBilling && (isPanCustomer || isPanMandatoryNow) && crossTabPan?.trim()) {
          if (!panCustomerInfo?.isValid) {
            Modal.warning({
              title: "PAN Not Validated",
              content: "Please validate PAN by pressing Enter in the PAN field before proceeding.",
              okText: "OK",
              cancelButtonProps: { style: { display: "none" } },
            });
            setIsSubmittingCustomer(false);
            isLocked.current = false;
            return;
          }
        }

        // ── NRI passport check (only when passport field is configured and visible) ──
        if (isNriBilling) {
          const hasPassportField = (retailCustomerFields || []).some((f) => {
            const fn = f.name?.toLowerCase().replace(/\s+/g, "");
            return fn?.startsWith("passport") && !fn?.includes("file") && f.visible;
          });
          if (hasPassportField) {
            const allFields = customerForm.getFieldsValue(true);
            const passportValue = Object.keys(allFields).find((k) => k.toLowerCase().startsWith("passport") && !k.toLowerCase().includes("file"));
            if (passportValue && !allFields[passportValue]?.trim()) {
              Modal.warning({
                title: "Passport Required",
                content: "Please enter Passport number to proceed with NRI Billing.",
                okText: "OK",
                cancelButtonProps: { style: { display: "none" } },
              });
              setIsSubmittingCustomer(false);
              isLocked.current = false;
              return;
            }
          }
        }

        // ── PAN file check ──
        if ((isPanCustomer || isPanMandatoryNow) && crossTabPan?.trim()) {
          const cartCustomer = useCartStore.getState().cart?.customer;
          const hasPanInDocuments = (documents || []).some((d) => d.docName === "PAN Card" && d.docUrl);
          const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
          const activeCart = isExchange ? useCartStore.getState().exchangeCart : cart;
          const panMeta = activeCart?.customer?.metaData?.find((m) => m.key === "PAN_CARD_URL")?.value || activeCart?.customer?.pANCARDURL;
          const hasExistingPanFile =
            currentTabFormValues?.contact?.panCardFile ||
            cartCustomer?.metaData?.find((m) => m.key === "PAN_CARD_URL")?.value ||
            cartCustomer?.metaData?.find((m) => m.key === "pANCARDURL ")?.value ||
            hasPanInDocuments;
          if (!hasExistingPanFile) {
            return new Promise((resolve) => {
              Modal.warning({
                title: "PAN File Required",
                content: "Please upload PAN file to proceed.",
                okText: "OK",
                okButtonProps: { style: { display: "inline-block" } },
                cancelButtonProps: { style: { display: "none" } },
                onOk: () => resolve(false),
              });
            });
          }
        } else if (isNriBilling) {
          const hasPassportFieldConfigured = (retailCustomerFields || []).some((f) => {
            const fn = f.name?.toLowerCase().replace(/\s+/g, "");
            return (fn?.startsWith("passport") || fn?.includes("passportfile")) && f.visible;
          });
          if (hasPassportFieldConfigured) {
            const hasPassportInDocuments = (documents || []).some((d) => d.docName === "Passport" && d.docUrl);
            const cartPassport = cart?.customer?.metaData?.find((m) => m.key === "PASSPORT_URL")?.value;
            const passportFile = currentTabFormValues?.contact?.passportFile || formDataCache?.[activeTab]?.contact?.passportFile || hasPassportInDocuments || cartPassport;
            if (!passportFile) {
              await new Promise((resolve) => {
                Modal.warning({
                  title: "Passport Upload Required",
                  content: "Please upload Passport file to proceed with NRI Billing.",
                  okText: "OK",
                  cancelButtonProps: { style: { display: "none" } },
                  onOk: () => resolve(false),
                });
              });
              setIsSubmittingCustomer(false);
              isLocked.current = false;
              return;
            }
          }
        }

        // ── GST-PAN cross-check (only when PAN/B2B enabled) ──
        if ((isPanCustomer && isB2BEnabled) && crossTabGst && crossTabPan) {
          const enteredPANVal = crossTabPan?.trim()?.toUpperCase();
          const gstPAN = crossTabGst?.substring(2, 12)?.toUpperCase();
          if (enteredPANVal !== gstPAN) {
            return new Promise((resolve) => {
              Modal.warning({
                title: "PAN & GST Mismatch",
                content: "The GST number entered does not match the provided PAN number. Please verify the GST number and enter correct details.",
                okText: "OK",
                cancelButtonProps: { style: { display: "none" } },
                onOk: () => resolve(false),
              });
            });
          }
        }

        // Tab-level required-field validation
        const failedTab = retailCustomerTabs
          .filter((tab) => tab.visible)
          .sort((a, b) => a.order - b.order)
          .find((tab) => {
            const tabCache = updatedCache[tab.id] || {};
            const formValues = customerForm.getFieldsValue(true);
            const mergedData = { ...tabCache, ...formValues };
            const isBusinessTab = tab.name?.toLowerCase().includes("business");
            if (isBusinessTab && !isB2BEnabled) return false;
            const currentTabFields = retailCustomerFields.filter((field) => field.tabId === tab.id && field.visible && field.mandatory);
            return currentTabFields.some((field) => {
              const fieldKey = toCamelCase(field.name);
              if (mergedData[fieldKey]?.toString().trim()) return false;
              const foundInGroup = Object.entries(mergedData).some(([key, groupData]) => /^group_\d+$/.test(key) && groupData?.[fieldKey]?.toString().trim());
              return !foundInGroup;
            });
          });

        if (failedTab) {
          setActiveTab(failedTab.id);
          setActiveTabName(failedTab.name);
          if (failedTab.id !== activeTab && updatedCache[failedTab.id]) {
            setTimeout(() => customerForm.setFieldsValue(updatedCache[failedTab.id]), 0);
          }
          setIsSubmittingCustomer(false);
          isLocked.current = false;
          return;
        }

        // Build merged form data — use updatedCache (not stale formDataCache)
        const allFormValues = { ...updatedCache };
        const formValues = customerForm.getFieldsValue(true);
        let mergedData = {};
        if (isPanUpdateMode || cart?.customer?.cwrCustomerId !== tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId) {
          mergedData = {
            ...Object.values(allFormValues).reduce((acc, tabValues) => ({ ...acc, ...tabValues }), {}),
            ...formValues,
          };
        } else {
          mergedData = Object.values(allFormValues).reduce((acc, tabValues) => ({ ...acc, ...tabValues }), {});
        }

        const isGroupKey = (key) => /^group_\d+$/.test(key);
        const convertKeysToCamelCase = (obj) => {
          if (Array.isArray(obj)) return obj.map(convertKeysToCamelCase);
          if (obj && typeof obj === "object") {
            return Object.entries(obj).reduce((acc, [key, value]) => {
              if (isGroupKey(key)) {
                acc[key] = convertKeysToCamelCase(value);
              } else {
                // acc[toCamelCase(key)] = convertKeysToCamelCase(value);
                const normalizedKey = key.replace(/\s+/g, "").toLowerCase();
                if (normalizedKey === "gsttype" && key !== "gstType") {
                  return acc;
                }
                const camelKey = toCamelCase(key);
                if (acc[camelKey] === undefined || acc[camelKey] === null || acc[camelKey] === "") {
                  acc[camelKey] = convertKeysToCamelCase(value);
                }
              }
              return acc;
            }, {});
          }
          return obj;
        };
        const camelCasedData = convertKeysToCamelCase(mergedData);
        // TCS: Attach PAN customer properties for downstream tax calculation
        if (isPanCustomer && panCustomerInfo?.isValid) {
          camelCasedData.customerProperties = {
            panNo: panCustomerInfo.panNo,
            panStatus: panCustomerInfo.panStatus,
            aadhaarSeedingStatusCode: panCustomerInfo.aadhaarSeedingStatusCode,
            isValid: panCustomerInfo.isValid,
            realStatus: panCustomerInfo.realStatus,
          };
        }

        // Static select normalization
        groupedFields.forEach((group) => {
          group.fields.forEach((field) => {
            if (field.type === "select" && field.dataSource?.type === "static" && field.defaultValue) {
              const fieldKey = toCamelCase(field.name);
              if (camelCasedData[fieldKey]) {
                camelCasedData[fieldKey] = normalizeStaticSelectValue(camelCasedData[fieldKey], field.defaultValue);
              }
              if (group.groupId && camelCasedData[group.groupId]?.[fieldKey]) {
                camelCasedData[group.groupId][fieldKey] = normalizeStaticSelectValue(camelCasedData[group.groupId][fieldKey], field.defaultValue);
              }
            }
          });
        });
        const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";

        const activeCart = isExchange ? useCartStore.getState().exchangeCart : cart;
        // Submit
        // BUG-1228: When cwrCustomerId is null/undefined, the customer is new — must use addNewCustomer.
        // Also respect isAddNewCustomer flag (user explicitly opened "Register New Customer" flow).
        const cartCustomerId = activeCart?.customer?.cwrCustomerId;
        const isNewCustomerFlow = (isAddNewCustomer && !isPanUpdateMode) || !cartCustomerId;
        let submitResult;
        if (useTransactionStore.getState().refundCustomerEdit === "Y") {
          submitResult = await addNewCustomer(camelCasedData);
          if (submitResult === false) {
            setIsSubmittingCustomer(false);
            isLocked.current = false;
            return;
          }
          useTransactionStore.getState().setRefundCustomerEdit(null);
        } else if (
          !isNewCustomerFlow &&
          (isPanUpdateMode ||
            (retailCustomerFields && retailCustomerFields.length > 0 && cartCustomerId !== tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId))
        ) {
          submitResult = await editOldCustomer(camelCasedData);
          // BUG-2370: "Go back and edit" in the review popup -- keep the form as typed.
          if (submitResult === false) {
            setIsSubmittingCustomer(false);
            isLocked.current = false;
            return;
          }
          const isDefaultCustomer = tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cartCustomerId;
          if (!isDefaultCustomer && posConfig?.requireSalesRepresentative === "Y") {
            const exchangeCartData = useCartStore.getState().exchangeCart;
            if (exchangeCartData) {
              if (exchangeCartData?.salesRepId === null) setOpenSalesRep({ flag: true, level: "order" });
            } else {
              if (activeCart?.salesRepId === null) setOpenSalesRep({ flag: true, level: "order" });
            }
          }
        } else {
          submitResult = await addNewCustomer(camelCasedData);
          if (submitResult === false) {
            setIsSubmittingCustomer(false);
            isLocked.current = false;
            return;
          }
        }

        // Reset form state — drawer closing is handled by createCustomer/updateCustomer
        // on success, so we only reset ancillary state here.
        customerForm.resetFields();
        setIsB2BEnabled(false);
        setIsPanUpdateMode(false);
        setFormDataCache({});
        setPanCustomerInfo(null);
        setActiveTab(retailCustomerTabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order)[0]?.id || "");
        setActiveTabName(retailCustomerTabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order)[0]?.name || "");
      } catch (err) {
        console.error("Error while registering customer:", err);
      } finally {
        setIsSubmittingCustomer(false);
        isLocked.current = false;
      }
    } else {
      await addCustomer();
    }
  };

  // When user explicitly opened the "Register New Customer" flow (isAddNewCustomer === true),
  // force register-mode regardless of any leftover cart.customer state from previous sessions.
  // BUG-1228: Also treat null/undefined cwrCustomerId as new customer (not update).
  const isUpdateMode =
    isPanUpdateMode ||
    (!isAddNewCustomer &&
      !!cart?.customer?.cwrCustomerId &&
      (isPanMandatoryNow ||
        (retailCustomerFields && retailCustomerFields.length > 0 && cart?.customer?.cwrCustomerId !== tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId)));
  const isRefundMode = useTransactionStore.getState().refundCustomerEdit === "Y";

  const buttonLabel = () => {
    if (isSubmittingCustomer) {
      return isRefundMode ? "Registering..." : isUpdateMode ? "Updating..." : "Registering...";
    }
    if (isRefundMode) return "Register Customer";
    if (isUpdateMode) return "Update Customer";
    return "Register Customer";
  };

  const isPanBlocking = isPanMandatoryNow && (!enteredPAN || enteredPAN.trim() === "");
  // Block submit when the entered PAN was validated and came back as invalid.
  // Without this, the Update Customer button stays clickable on INVALID PAN
  // and upsertb2ccustomer is called with bad data.
  const isPanInvalid = isPanCustomer && panCustomerInfo && panCustomerInfo.isValid === false;
  const isSubmitDisabled = isSubmittingCustomer || isPanInvalid;

  const fileInputRef = useRef(null);

  const existingDocUrls = useMemo(() => {
    const meta = cart?.customer?.metaData || [];
    const docMeta = meta.find((m) => m.key === "CUSTOMER_DOCUMENT_URLS");
    if (!docMeta?.value) return [];
    try {
      return JSON.parse(docMeta.value);
    } catch {
      return [];
    }
  }, [cart?.customer?.metaData]);

  const newDocuments = (documents || []).filter((d) => d?.docUrl && d.docName === "Customer Document");

  const allDocumentList = useMemo(() => {
    const list = [];
    existingDocUrls.forEach((url, i) => {
      list.push({ key: `existing-${i}`, url, fileName: `Document ${i + 1}`, source: "existing" });
    });
    newDocuments.forEach((doc, i) => {
      if (!existingDocUrls.includes(doc.docUrl)) {
        list.push({ key: `new-${i}`, url: doc.docUrl, fileName: doc.fileName || `New Document ${i + 1}`, source: "new" });
      }
    });
    return list;
  }, [existingDocUrls, newDocuments]);

  const handleDocumentFileUpload = async (e) => {
    if (handleFileUpload) {
      await handleFileUpload(e, "Customer Document");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeNewDocument = (docUrl) => {
    if (setDocuments) {
      setDocuments((prev) => prev.filter((d) => !(d.docName === "Customer Document" && d.docUrl === docUrl)));
    }
  };

  const renderDocumentModal = () => (
    <Modal
      open={uploadModalVisible}
      title="Customer Documents"
      onCancel={() => setUploadModalVisible(false)}
      footer={[
        <Button key="close" onClick={() => setUploadModalVisible(false)}>
          Close
        </Button>,
      ]}
      width={520}
    >
      {allDocumentList.length === 0 && <p style={{ color: "#6B7280", textAlign: "center", padding: "16px 0" }}>No documents uploaded yet.</p>}
      {allDocumentList.length > 0 && (
        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          {allDocumentList.map((doc) => (
            <div
              key={doc.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 }}>
                <FileOutlined style={{ color: "#2F3856", fontSize: "16px" }} />
                <span style={{ fontSize: "13px", color: "#344054", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.fileName}</span>
                {doc.source === "new" && <span style={{ fontSize: "11px", color: "#16a34a", fontWeight: 600 }}>New</span>}
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <EyeOutlined
                  style={{ fontSize: "16px", color: "#2F3856", cursor: "pointer" }}
                  title="View document"
                  onClick={() => openDocument(doc.url)}
                />
                {!isViewMode && doc.source === "new" && (
                  <DeleteOutlined style={{ fontSize: "14px", color: "#B42318", cursor: "pointer" }} onClick={() => removeNewDocument(doc.url)} title="Remove document" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {!isViewMode && (
        <div style={{ marginTop: "16px", textAlign: "center" }}>
          <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display: "none" }} onChange={handleDocumentFileUpload} />
          <Button icon={<UploadOutlined />} style={{ border: "1px solid #2F3856", color: "#2F3856" }} onClick={() => fileInputRef.current?.click()}>
            Upload New Document
          </Button>
        </div>
      )}
    </Modal>
  );

  return (
    <>
      {renderDocumentModal()}
      <hr style={{ border: "none", borderTop: "1px solid #d1d5db", marginBottom: "16px" }} />
      <Row justify="space-between" style={{ marginTop: "2vh" }}>
        <Col>
          <Button
            style={{ padding: "6px 16px", border: "1px solid #2F3856", height: "6vh", background: "#fff", borderRadius: "6px", color: "#2F3856", fontWeight: 500 }}
            onClick={() => setUploadModalVisible(true)}
          >
            {isViewMode ? "View Documents" : "Upload Documents"}
            {allDocumentList.length > 0 && (
              <span style={{ marginLeft: "6px", background: "#2F3856", color: "#fff", borderRadius: "10px", padding: "1px 7px", fontSize: "11px" }}>{allDocumentList.length}</span>
            )}
          </Button>
        </Col>
        <Col style={{ display: "flex", gap: "12px" }}>
          <Col>
            <Button
              style={{
                padding: "6px 16px",
                border: "1px solid #ccc",
                height: "6vh",
                background: "#fff",
                borderRadius: "6px",
                cursor: isPanBlocking ? "not-allowed" : "pointer",
              }}
              disabled={isPanBlocking}
              onClick={() => setDisplayAddNewCustomer(false)}
            >
              Cancel
            </Button>
          </Col>
          <Col>
            <button
              style={{
                padding: "6px 16px",
                background: isSubmitDisabled ? "#999" : "#2F3856",
                height: "6vh",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontWeight: "bold",
                cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "160px",
                gap: "10px",
              }}
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
            >
              {isSubmittingCustomer ? (
                <>
                  <span className="loader" />
                  {buttonLabel()}
                </>
              ) : (
                buttonLabel()
              )}
            </button>
          </Col>
        </Col>
      </Row>
      <Modal
        open={docViewer.visible}
        onCancel={closeDocViewer}
        footer={null}
        width="85vw"
        style={{ top: 20 }}
        title="Document Preview"
        destroyOnClose
      >
        {docViewer.url && (
          docViewer.isPdf ? (
            <iframe src={docViewer.url} title="Document" style={{ width: "100%", height: "75vh", border: "none" }} />
          ) : (
            <img src={docViewer.url} alt="Document" style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", display: "block", margin: "0 auto" }} />
          )
        )}
      </Modal>
    </>
  );
};

export default CustomerDrawerFooter;
